import { useState, useEffect } from 'react';
import type { Team, Employee, EmployeeStatus } from '../types';
import {
  getTeams,
  getEmployees,
  updateEmployeeTeam,
  updateEmployeeVisibility,
  updateEmployeeStatus,
  uploadEmployeePhoto,
  deleteEmployeePhoto,
  getSyncStatus,
  runQuickSync
} from '../services/api';
import type { SyncStatus, SyncResult } from '../services/api';
import { PhotoUpload } from './PhotoUpload';

type FilterOption = 'all' | 'unassigned' | 'hidden' | string;

export function TeamManagement() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsData, employeesData, statusData] = await Promise.all([
        getTeams(),
        getEmployees(),
        getSyncStatus(),
      ]);
      setTeams(teamsData);
      setEmployees(employeesData);
      setSyncStatus(statusData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = async (employeeId: string, teamId: string) => {
    setUpdating(employeeId);
    try {
      const updated = await updateEmployeeTeam(employeeId, teamId || null);
      setEmployees(prev => prev.map(e => e.id === employeeId ? updated : e));
    } catch (error) {
      console.error('Error updating team:', error);
      alert('Failed to update team assignment');
    } finally {
      setUpdating(null);
    }
  };

  const handleVisibilityChange = async (employeeId: string, isHidden: boolean) => {
    setUpdating(employeeId);
    try {
      const updated = await updateEmployeeVisibility(employeeId, isHidden);
      setEmployees(prev => prev.map(e => e.id === employeeId ? updated : e));
    } catch (error) {
      console.error('Error updating visibility:', error);
      alert('Failed to update visibility');
    } finally {
      setUpdating(null);
    }
  };

  const handleStatusChange = async (employeeId: string, status: EmployeeStatus) => {
    setUpdating(employeeId);
    try {
      const updated = await updateEmployeeStatus(employeeId, status);
      setEmployees(prev => prev.map(e => e.id === employeeId ? updated : e));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const handlePhotoUpload = async (employeeId: string, photo: string, contentType: string) => {
    setUpdating(employeeId);
    try {
      const updated = await uploadEmployeePhoto(employeeId, photo, contentType);
      setEmployees(prev => prev.map(e => e.id === employeeId ? updated : e));
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUpdating(null);
    }
  };

  const handlePhotoDelete = async (employeeId: string) => {
    setUpdating(employeeId);
    try {
      const updated = await deleteEmployeePhoto(employeeId);
      setEmployees(prev => prev.map(e => e.id === employeeId ? updated : e));
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    } finally {
      setUpdating(null);
    }
  };

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await runQuickSync();
      setSyncResult(result);
      // Reload data after sync
      await loadData();
    } catch (error) {
      console.error('Sync error:', error);
      alert('Sync failed. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  const filteredEmployees = employees.filter(e => {
    if (filter === 'all') return !e.isHidden; // By default, show non-hidden
    if (filter === 'hidden') return e.isHidden;
    if (filter === 'unassigned') return !e.teamId && !e.isHidden;
    return e.teamId === filter && !e.isHidden;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    // Unassigned first, then by name
    if (!a.teamId && b.teamId) return -1;
    if (a.teamId && !b.teamId) return 1;
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  });

  const unassignedCount = employees.filter(e => !e.teamId && !e.isHidden).length;
  const hiddenCount = employees.filter(e => e.isHidden).length;
  const visibleCount = employees.filter(e => !e.isHidden).length;

  const getStatusBadgeClass = (status: EmployeeStatus) => {
    switch (status) {
      case 'ACTIVE': return 'status-badge-active';
      case 'ON_LEAVE': return 'status-badge-leave';
      case 'TERMINATED': return 'status-badge-terminated';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <div>Loading team data...</div>
      </div>
    );
  }

  return (
    <div className="team-management">
      {/* Sync Section */}
      <div className="sync-section">
        <div className="sync-info">
          <h3>HouseCallPro Sync</h3>
          <p>
            Last sync: {syncStatus?.lastSuccessfulSync
              ? new Date(syncStatus.lastSuccessfulSync).toLocaleString()
              : 'Never'}
          </p>
          <p className="record-counts">
            Records: {syncStatus?.recordCounts.employees} employees, {syncStatus?.recordCounts.jobs} jobs, {syncStatus?.recordCounts.estimates} estimates
          </p>
          {syncStatus?.autoSync && (
            <p className="auto-sync-status">
              Auto-sync: {syncStatus.autoSync.enabled ? `Enabled (${syncStatus.autoSync.scheduleDescription})` : 'Disabled'}
            </p>
          )}
        </div>
        <button
          className="sync-button"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {syncResult && (
        <div className={`sync-result ${syncResult.success ? 'success' : 'error'}`}>
          {syncResult.success ? (
            <span>Synced {syncResult.jobs} jobs, {syncResult.estimates} estimates in {(syncResult.duration / 1000).toFixed(1)}s</span>
          ) : (
            <span>Sync failed: {syncResult.error}</span>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="filter-row">
        <label>Filter:</label>
        <select value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">Active Employees ({visibleCount})</option>
          <option value="unassigned">Unassigned ({unassignedCount})</option>
          <option value="hidden">Hidden ({hiddenCount})</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>
              {team.name} ({employees.filter(e => e.teamId === team.id && !e.isHidden).length})
            </option>
          ))}
        </select>
      </div>

      {/* Employee Table */}
      <div className="employee-table-container">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Employee</th>
              <th>Status</th>
              <th>Team</th>
              <th>Visible</th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map(employee => (
              <tr
                key={employee.id}
                className={`${!employee.teamId ? 'unassigned' : ''} ${employee.isHidden ? 'hidden-row' : ''}`}
              >
                <td className="photo-cell">
                  <PhotoUpload
                    currentPhoto={employee.customPhoto}
                    hcpAvatar={employee.avatar}
                    employeeName={`${employee.firstName} ${employee.lastName}`}
                    onUpload={(photo, contentType) => handlePhotoUpload(employee.id, photo, contentType)}
                    onDelete={() => handlePhotoDelete(employee.id)}
                  />
                </td>
                <td className="employee-info">
                  <div className="employee-name">{employee.firstName} {employee.lastName}</div>
                  <div className="employee-role">{employee.role || 'No role'}</div>
                </td>
                <td className="status-cell">
                  <select
                    value={employee.status}
                    onChange={e => handleStatusChange(employee.id, e.target.value as EmployeeStatus)}
                    disabled={updating === employee.id}
                    className={`status-select ${getStatusBadgeClass(employee.status)}`}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="ON_LEAVE">On Leave</option>
                    <option value="TERMINATED">Terminated</option>
                  </select>
                </td>
                <td>
                  <select
                    value={employee.teamId || ''}
                    onChange={e => handleTeamChange(employee.id, e.target.value)}
                    disabled={updating === employee.id}
                    className={updating === employee.id ? 'updating' : ''}
                  >
                    <option value="">-- Unassigned --</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </td>
                <td className="visibility-cell">
                  <button
                    onClick={() => handleVisibilityChange(employee.id, !employee.isHidden)}
                    disabled={updating === employee.id}
                    className={`visibility-btn ${employee.isHidden ? 'is-hidden' : 'is-visible'}`}
                    title={employee.isHidden ? 'Show on dashboard' : 'Hide from dashboard'}
                  >
                    {employee.isHidden ? 'Show' : 'Hide'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedEmployees.length === 0 && (
        <div className="empty-state">
          <div>No employees match the current filter</div>
        </div>
      )}
    </div>
  );
}
